import { toMoment } from '@core/utils/time';

/** Company holidays as 'YYYY-MM-DD' keys — every covered day is treated exactly like a weekend. */
export type HolidaySet = Set<string>;

export function expandHolidays(holidays: { startDate: string; endDate: string; isNonWorking?: boolean }[]): HolidaySet {
  const set: HolidaySet = new Set();
  // Informational holidays (isNonWorking false) are shown on calendars but never skipped
  holidays.filter(h => h.isNonWorking !== false).forEach(h => {
    const cursor = toMoment(h.startDate).startOf('day');
    const last = toMoment(h.endDate || h.startDate).startOf('day');
    while (cursor.isSameOrBefore(last, 'day')) {
      set.add(cursor.format('YYYY-MM-DD'));
      cursor.add(1, 'day');
    }
  });
  return set;
}

export function isWorkingDay(day: number, holidays: HolidaySet = new Set()): boolean {
  const m = toMoment(day);
  return m.day() !== 0 && m.day() !== 6 && !holidays.has(m.format('YYYY-MM-DD'));
}

/**
 * Signed number of WORKING days from `from` to `to`, mirroring
 * `CalendarEventImportService::businessDaysBetween` (the exact inverse of the server's
 * addBusinessDays): working days in (from, to] going forward or [to, from) going backward — the
 * target day always counts, the reference day never does.
 */
export function businessDaysBetween(from: number, to: number, holidays: HolidaySet = new Set()): number {
  const a = toMoment(from).startOf('day');
  const b = toMoment(to).startOf('day');
  const step = b.isBefore(a, 'day') ? -1 : 1;
  let count = 0;
  const cursor = a.clone();
  while (!cursor.isSame(b, 'day')) {
    cursor.add(step, 'day');
    if (isWorkingDay(cursor.unix(), holidays)) count++;
  }
  return step * count;
}

/** First working day on or after `day`, keeping the time of day (a Saturday drop lands on Monday). */
export function snapToWorkingDay(day: number, holidays: HolidaySet = new Set()): number {
  const m = toMoment(day);
  while (!isWorkingDay(m.unix(), holidays)) m.add(1, 'day');
  return m.unix();
}

/** Working days covered by [start, end] inclusive by day, at least 1. */
export function countBusinessDays(start: number, end: number, holidays: HolidaySet = new Set()): number {
  let count = 0;
  const cursor = toMoment(start).startOf('day');
  const last = toMoment(end).startOf('day');
  while (cursor.isSameOrBefore(last, 'day')) {
    if (isWorkingDay(cursor.unix(), holidays)) count++;
    cursor.add(1, 'day');
  }
  return Math.max(1, count);
}
